import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";

import { useGlobal } from "../GlobalContext.js";
import { search, searchApk } from "../services/queryService.js";
import defaultImage from "assets/img/default_icon.png";

/**
 * Buscador principal de apps.
 *
 * Objetivos:
 * - preservar la funcionalidad nueva de main:
 *   - limpiar app seleccionada
 *   - searchedApp
 *   - showNoResult
 * - mantener las mejoras responsive
 * - evitar dobles cargas del fetch inicial
 * - reutilizar apkData del contexto si ya existe
 */
const useStyles = makeStyles((theme) => ({
  searchContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
  },

  autocompleteRoot: {
    width: "100%",
  },

  searchInput: {
    width: "100%",
  },

  textFieldRoot: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      width: "100%",
    },
  },

  clearIndicator: {
    transform: "translateX(-50%)",
  },

  listbox: {
    maxHeight: 360,
    overflowY: "auto",

    [theme.breakpoints.down("sm")]: {
      maxHeight: 280,
    },
  },

  optionRow: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    minWidth: 0,
  },

  optionIcon: {
    width: 32,
    height: 32,
    marginRight: theme.spacing(1.25),
    borderRadius: 6,
    objectFit: "cover",
    flexShrink: 0,

    [theme.breakpoints.down("sm")]: {
      width: 28,
      height: 28,
      marginRight: theme.spacing(1),
    },
  },

  optionText: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
  },
}));

const SearchBar = () => {
  const classes = useStyles();
  const { t } = useTranslation("search");
  const { updateSharedValue, sharedValue, clearSearchData } = useGlobal();

  const { id, apkData } = sharedValue || {};
  const isLoggedIn = Boolean(id);

  const [records, setRecords] = useState(Array.isArray(apkData) ? apkData : []);
  const [searchText, setSearchText] = useState("");

  /**
   * Evita dobles cargas en montaje, especialmente en desarrollo con StrictMode.
   */
  const didFetchRef = useRef(false);

  const clearSelectedApp = () => {
    if (typeof clearSearchData === "function") {
      clearSearchData();
      return;
    }

    updateSharedValue("appName", null);
    updateSharedValue("packageName", null);
    updateSharedValue("searchedApp", "");
    updateSharedValue("showNoResult", false);
  };

  useEffect(() => {
    /**
     * Si ya tenemos datos cacheados en el contexto, los reutilizamos
     * y evitamos volver a pedir todo al backend.
     */
    if (Array.isArray(apkData) && apkData.length > 0) {
      setRecords(apkData);
      return;
    }

    /**
     * Guard para impedir ejecuciones repetidas del fetch inicial.
     */
    if (didFetchRef.current) return;
    didFetchRef.current = true;

    const fetchData = async () => {
      try {
        const payloadAnalyses = { campos: { package_name: 1 } };
        const analyses = await search(payloadAnalyses);

        const packages = (analyses || [])
          .filter((item) => item?.package_name)
          .map((item) => ({ package: item.package_name }));

        if (packages.length === 0) {
          setRecords([]);
          updateSharedValue("apkData", []);
          updateSharedValue("reqError", "");
          return;
        }

        const payloadApks = {
          filtro: { $or: packages },
          campos: {
            title: 1,
            package: 1,
            iconUrl: 1,
            description: 1,
            ratings: 1,
            realInstalls: 1,
            reviews: 1,
            score: 1,
            developer: 1,
            developerEmail: 1,
            developerWebsiteUrl: 1,
          },
        };

        const result = await searchApk(payloadApks);
        const normalizedResult = Array.isArray(result) ? result : [];

        setRecords(normalizedResult);
        updateSharedValue("apkData", normalizedResult);
        updateSharedValue("reqError", "");
      } catch (err) {
        console.error(err);
        setRecords([]);
        updateSharedValue("reqError", err?.message || String(err));
      }
    };

    fetchData();
  }, [apkData, updateSharedValue]);

  /**
   * Debounce para decidir si hay coincidencia exacta.
   * Esto preserva el comportamiento de main para "app no encontrada".
   */
  useEffect(() => {
    const handler = setTimeout(() => {
      const normalizedSearch = searchText.trim().toLowerCase();

      if (!normalizedSearch) {
        updateSharedValue("showNoResult", false);
        return;
      }

      const match = records.some(
        (record) =>
          record?.title?.trim()?.toLowerCase() === normalizedSearch
      );

      updateSharedValue("showNoResult", !match);
    }, 2000);

    return () => clearTimeout(handler);
  }, [searchText, records, updateSharedValue]);

  /**
   * Filtrado local por título.
   * Solo mostramos opciones si el usuario tiene sesión iniciada.
   */
  const filteredOptions = useMemo(() => {
    if (!isLoggedIn) return [];

    const normalizedSearch = searchText.trim().toLowerCase();

    if (!normalizedSearch) return records;

    return records.filter((record) =>
      record?.title?.toLowerCase().includes(normalizedSearch)
    );
  }, [isLoggedIn, records, searchText]);

  const handleChange = (event, newValue) => {
    if (!newValue) {
      clearSelectedApp();
      return;
    }

    if (typeof newValue === "string") {
      const normalizedValue = newValue.trim().toLowerCase();
  
      const match = records.find(
        (record) =>
          record?.title?.trim()?.toLowerCase() === normalizedValue
      );
  
      if (match) {
        updateSharedValue("appName", match.title || null);
        updateSharedValue("packageName", match.package || null);
        updateSharedValue("searchedApp", match.title || "");
        updateSharedValue("showNoResult", false);
      } else {
        updateSharedValue("appName", null);
        updateSharedValue("packageName", null);
        updateSharedValue("searchedApp", newValue);
        updateSharedValue("showNoResult", true);
      }
  
      return;
    }

    if (typeof newValue === "object") {
      if (!isLoggedIn) return;
  
      updateSharedValue("appName", newValue.title || null);
      updateSharedValue("packageName", newValue.package || null);
      updateSharedValue("searchedApp", newValue.title || "");
      updateSharedValue("showNoResult", false);
    }
  };

  const handleInputChange = (event, newInputValue) => {
    setSearchText(newInputValue);
    updateSharedValue("searchedApp", newInputValue);

    if (!newInputValue || newInputValue.trim() === "") {
      clearSelectedApp();
    }
  };

  return (
    <div className={classes.searchContainer}>
      <Autocomplete
        classes={{
          root: classes.autocompleteRoot,
          listbox: classes.listbox,
          clearIndicator: classes.clearIndicator,
        }}
        freeSolo
        fullWidth
        autoHighlight
        openOnFocus
        options={filteredOptions}
        getOptionLabel={(option) => {
          if (typeof option === "string") return option;
          return option?.title || "";
        }}
        onInputChange={handleInputChange}
        onChange={handleChange}
        renderOption={(option) => (
          <div className={classes.optionRow}>
            <img
              src={option?.iconUrl || defaultImage}
              alt={option?.title || "App icon"}
              className={classes.optionIcon}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = defaultImage;
              }}
            />
            <span className={classes.optionText}>{option?.title || ""}</span>
          </div>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={t("buscar-app")}
            className={classes.textFieldRoot}
            InputProps={{
              ...params.InputProps,
              className: classes.searchInput,
              "aria-label": "Search",
              inputProps: {
                ...params.inputProps,
                onFocus: (e) => {
                  setTimeout(() => e.target.select(), 0);
                },
              },
            }}
          />
        )}
      />
    </div>
  );
};

export default SearchBar;